import axiosClient from './axiosClient';
import { QuizAttempt } from '../types';

/**
 * Quizzes belonging to a course, keyed by the module they assess.
 *
 * The player renders a module assessment from `module.quiz` — an embedded object
 * that is empty on every course in the database. The actual questions live in the
 * `quizzes` collection, which had no endpoint to reach them by module, so 1,796
 * written questions were unreachable from the app. This is what connects them.
 *
 * The response carries no answer key; grading happens server-side on submit.
 */
export const fetchQuizzesByCourseApi = async (courseId: string): Promise<any[]> => {
  try {
    const response = await axiosClient.get(`/quizzes`, { params: { courseId } });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    // A course with no quizzes is a normal state, not a failure worth breaking
    // the player over.
    console.error('Error fetching quizzes for course:', error);
    return [];
  }
};

export const startQuizAttemptApi = async (
  quizId: string,
  payload: { courseId?: string; lessonId?: string }
): Promise<{ id: string }> => {
  const response = await axiosClient.post(`/quizzes/${quizId}/attempt`, payload);
  return { id: response.data.id ?? response.data._id };
};

/** Submits answers for grading. The verdict comes back from the server. */
export const submitQuizAttemptApi = async (
  attemptId: string,
  answers: Array<{ questionId: string; selectedAnswer: string | string[] }>,
  timeSpent?: number
): Promise<{
  id: string;
  score: number;
  totalPossibleScore: number;
  percentageScore: number;
  passingScore: number;
  passed: boolean;
  answers: Array<{ questionId: string; selectedAnswer: unknown; isCorrect: boolean; pointsEarned: number }>;
  review?: Array<{ questionId: string; correctAnswer?: string; correctAnswers?: string[]; explanation?: string }>;
}> => {
  const response = await axiosClient.post(`/quizzes/${attemptId}/submit`, { answers, timeSpent });
  return response.data;
};

export const fetchQuizAttemptsApi = async (uid: string): Promise<QuizAttempt[]> => {
  try {
    const response = await axiosClient.get(`/users/${uid}/quiz-attempts`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      return [];
    }
    console.error("Error fetching quiz attempts:", error);
    throw error;
  }
};

export const recordQuizAttemptApi = async (uid: string, attemptData: Omit<QuizAttempt, "id" | "startedAt">): Promise<QuizAttempt> => {
  try {
    const response = await axiosClient.post(`/users/${uid}/quiz-attempts`, attemptData);
    return response.data;
  } catch (error) {
    console.error("Error recording quiz attempt:", error);
    throw error;
  }
};

export const deleteQuizAttemptApi = async (attemptId: string): Promise<void> => {
  // Was `/quiz-attempts/:id`, which is mounted nowhere and 404'd on every call.
  await axiosClient.delete(`/quizzes/attempts/${attemptId}`);
};
